import { useEffect } from "react";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { getThemeTokens, type ThemeTokens } from "@/lib/content.functions";

export const themeTokensQuery = queryOptions({
  queryKey: ["cms", "theme"],
  queryFn: () => getThemeTokens(),
  staleTime: 60_000,
  refetchInterval: 60_000,
});

const KEY_RE = /^[a-zA-Z0-9_-]+$/;
// Disallow CSS control characters that could break out of the declaration:
// braces, semicolons, at-rules, comments, and dangerous functions.
const VALUE_BLOCKLIST = /[{};<>]|\/\*|\*\/|@import|expression\s*\(|url\s*\(|javascript:/i;

function isSafeValue(v: string): boolean {
  if (v.length > 512) return false;
  return !VALUE_BLOCKLIST.test(v);
}

function buildCss(tokens: ThemeTokens): string {
  const decls = Object.entries(tokens)
    .filter(
      ([k, v]) =>
        k && KEY_RE.test(k) && typeof v === "string" && v.length > 0 && isSafeValue(v),
    )
    .map(([k, v]) => `  --${k}: ${v};`)
    .join("\n");
  return `:root {\n${decls}\n}`;
}

const STYLE_ID = "cms-theme-tokens";

export function ThemeInjector() {
  const { data } = useSuspenseQuery(themeTokensQuery);
  const tokens = data?.tokens ?? {};

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!tokens || Object.keys(tokens).length === 0) return;
    let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement("style");
      el.id = STYLE_ID;
      el.setAttribute("data-theme", "cms");
      document.head.appendChild(el);
    }
    el.textContent = buildCss(tokens);
  }, [tokens]);

  // SSR: render the <style> inline inside body (HeadContent is fixed at SSR);
  // the effect above takes over on the client and keeps it in <head>.
  if (!tokens || Object.keys(tokens).length === 0) return null;
  return (
    <style
      data-theme="cms"
      id={STYLE_ID}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: buildCss(tokens) }}
    />
  );
}
